<template>
  <div class="gs-wizard-card gs-pixels-tab">
    <div class="gs-pixels-tab__header">
      <h2>{{ $t('frontend_facebook_conversion_api_detail') }}</h2>
      <gs-button
        size="auto"
        variant="primary"
        @click="createPixel"
      >
        <plus-icon class="w-5 h-5 fill-current" />
        {{ $t('add_new_pixel') }}
      </gs-button>
    </div>
    <!-- Pixel List -->
    <div
      v-if="fb_conversion_detail_list.length"
      id="links-list-view"
    >
      <v-client-table
        ref="linksTable"
        :data="fb_conversion_detail_list"
        :columns="tableColumns"
        :options="tableOptions"
        class="gs-pixels-table"
      >
        <div slot="actions" slot-scope="props">
          <!-- Edit Button -->
          <div class="flex flex-row">
            <gs-button
              variant="ghost"
              size="icon"
              class="mr-2"
              @click="editDetail(props.row.id)"
            >
              <pencil-icon class="w-5 h-5 fill-current" />
            </gs-button>
            <!-- Send Test Event Button -->
            <gs-button
              variant="ghost"
              size="icon"
              class="mr-2"
              @click="sendTestEvent(props.row.id)"
              :title="$t('frontend_groovesell_send_test_event')"
            >
              <paper-plane-icon class="w-5 h-5 fill-current" />
            </gs-button>
            <!-- Delete Button -->
            <gs-button
              variant="ghost"
              size="icon"
              class="mr-2 is-destructive"
              @click="openConfirmModal(props.row.id)"
            >
              <trash-icon class="w-5 h-5 fill-current" />
            </gs-button>
          </div>
        </div>
      </v-client-table>
    </div>
    <!-- No Pixel Message -->
    <div v-else class="gs-pixels-empty">
      {{ $t('frontend_no_pixel_added') }}
    </div>

    <facebook-pixel-editor
      v-if="showPixelEditor"
      :pixel="fb_conversion_detail"
      :pixel-id="is_editing ? editing_id : null"
      @close="showPixelEditor = false"
      @saved="pixelSaved"
    />
    <confirm-modal
      name="confirm-conversion-delete-modal"
      :message-content="$t('frontend_non_revertable')"
      :button-text="$t('frontend_delete_confirm')"
      button-size="auto"
      @confirm="deleteDetail"
    />
    <popup-alert v-if="alertProps.show" :variant="alertProps.variant" @close="alertProps.show = false">
      <template v-slot:title>
        {{ alertProps.title }}
      </template>
      <p>{{ alertProps.message }}</p>
    </popup-alert>
  </div>
</template>

<style>
.gs-pixels-tab__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.gs-pixels-tab__header > h2 {
  margin: 0;
  color: var(--gs-foreground);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

.gs-pixels-table {
  overflow: hidden;
  color: var(--gs-foreground);
  text-align: center;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-pixels-table tbody tr {
  border-top: 1px solid var(--gs-border);
}

.gs-pixels-table tbody tr:first-child {
  border-top: none;
}

.gs-pixels-empty {
  color: var(--gs-muted-foreground);
  font-size: 14px;
}

</style>

<script>
import Swal from 'sweetalert2';
import axios from 'axios';
import Notify from '@groovepages/gd-ui-kit/notify';
import FacebookPixelEditor from '@/components/FacebookPixelEditor.vue';
import PencilIcon from '@/assets/images/icons/PencilIcon.svg';
import TrashIcon from '@/assets/images/icons/TrashIcon.svg';
import PaperPlaneIcon from '@/assets/images/icons/PaperPlaneIcon.svg';
import PlusIcon from '@/assets/images/icons/PlusIcon.svg';

export default {
  components: {
    FacebookPixelEditor,
    PencilIcon,
    TrashIcon,
    PaperPlaneIcon,
    PlusIcon,
  },
  data() {
    return {
      alertProps: {
        show: false,
        variant: 'success',
        title: '',
        message: '',
      },
      fbConversionId: null,
      showPixelEditor: false,
      is_editing: false,
      editing_id: null,
      fb_conversion_detail_list: [],
      fb_conversion_detail: {},
      tableColumns: ['name', 'actions'],
      tableOptions: {
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        sortable: [],
        filterByColumn: false,
        filterable: false,
        childRowTogglerFirst: false,
        perPage: 10000,
        rowClassCallback() {
          return 'hover:bg-grey-50 funnelRow';
        },
      },
    }
  },
  async created() {
    this.$loader.start('fetchList');

    await this.getList();

    this.$loader.end('fetchList');
  },
  methods: {
    createPixel() {
      this.fb_conversion_detail = {};
      this.is_editing = false;
      this.editing_id = null;
      this.showPixelEditor = true;
    },
    async editDetail(Id) {
      this.$loader.start('editDetail');
      try {
        const { data } = await axios.post('/conversion-settings', { list: false, id: Id });
        this.fb_conversion_detail = data;
        this.editing_id = Id;
        this.is_editing = true;
        this.showPixelEditor = true;
      } catch (_) {
        Notify.fail('Unable to load this pixel. Please try again.');
      } finally {
        this.$loader.end('editDetail');
      }
    },
    pixelSaved(pixel) {
      const index = this.fb_conversion_detail_list.findIndex(item => String(item.id) === String(pixel.id));
      if (index === -1) this.fb_conversion_detail_list.unshift(pixel);
      else this.$set(this.fb_conversion_detail_list, index, pixel);
      this.fb_conversion_detail = {};
      this.showPixelEditor = false;
      Notify.success(this.$t('saved_settings'));
    },
    async sendTestEvent(Id) {
      Swal.fire({
        title: '',
        text: this.$t('enter_test_event_name'),
        input: 'text',
        inputAttributes: {
          autocapitalize: 'off',
        },
        showCancelButton: true,
        confirmButtonText: 'Send',
        showLoaderOnConfirm: true,
        inputValidator: (value) => {
          return !value && this.$t('you_need_to_write_something');
        },
        preConfirm: (eventName) => {
          return axios
            .post(`/send-test-event`, {
              event_name: eventName,
              pixel_id: Id,
            })
            .then((response) => {
              if (!response.data.events_received) {
                throw new Error(response.statusText);
              }
            })
            .catch((error) => {
              Swal.showValidationMessage(`${this.$t('request_failed')} ${error}`);
            });
        },
        allowOutsideClick: () => !Swal.isLoading(),
      }).then((result) => {
        if (result.value) {
          this.alertProps = {
            show: true,
            variant: 'success',
            title: '',
            message: this.$t('test_event_sent'),
          };
        }
      });
    },
    async getList() {
      const fbSetting = await axios.post('/conversion-settings', {
        list: true,
      });
      this.fb_conversion_detail_list = fbSetting.data.data;
    },
    openConfirmModal(fbConversionId) {
      this.fbConversionId = fbConversionId;
      this.$root.$emit('modal-open', 'confirm-conversion-delete-modal');
    },
    async deleteDetail() {
      this.$loader.start('deleteDetail');
      axios
        .delete(`/delete-conversion-settings/${this.fbConversionId}`)
        .then(() => {
          this.getList();
          this.$loader.end('deleteDetail');
        })
        .catch((error) => {
          console.log(error);
        });
    },
  },

};
</script>
